import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';

import { GtmService } from '@fcom/common/gtm';
import { LanguageService } from '@fcom/ui-translate';
import { AppState, ConfigService, CountryState } from '@fcom/core';
import { loginIsOpen, loginStatus } from '@fcom/core/selectors';
import { LoginStatus, LoginStep } from '@fcom/core-api/login';
import { isPresent, stopPropagation } from '@fcom/core/utils';
import { allowedConfs } from '@fcom/core/reducers/langs';
import { finShare, snapshot } from '@fcom/rx';
import { CmsTemplate, CmsViewType } from '@fcom/core-api/interfaces';
import { ButtonMode, ButtonSize, ButtonTheme, IconPosition } from '@fcom/ui-components';
import { LoginActions } from '@fcom/core/actions';

import { CmsPageService, MediaQueryService, NavigationMenuService } from '../../services';
import {
  BreadcrumbItem,
  ConsentStatus,
  ElementActions,
  ElementTypes,
  GaContext,
  MenuItem,
  MenuJson,
} from '../../interfaces';
import { ConsentService, JoinService, LoginService, LoginWidgetComponent } from '../../login';

@Component({
  selector: 'fin-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit {
  @Input()
  showBreadcrumbs = true;

  @ViewChild(LoginWidgetComponent, { static: false }) loginWidget: LoginWidgetComponent;

  readonly ButtonSize = ButtonSize;
  readonly IconPosition = IconPosition;
  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly ElementTypes = ElementTypes;
  readonly GaContext = GaContext;
  readonly homeLink$: Observable<string>;
  readonly currentLanguage$: Observable<string>;
  readonly LoginStatus = LoginStatus;
  readonly enableWelcomeBackMessage: boolean = false;
  readonly isVisible$: Observable<boolean>;
  isMenuOpen$: Observable<boolean>;
  countries$: Observable<CountryState>;
  loginStatus$: Observable<LoginStatus>;
  oneworldLink$: Observable<BreadcrumbItem>;

  menuItems$: Observable<MenuJson>;
  expandedCategory$: Observable<MenuItem>;
  expandedOrActiveNaviCategory$: Observable<MenuItem>;
  activeNaviCategory$: Observable<MenuItem>;
  isOpen = false;
  pageHasDisruptions$: Observable<boolean>;
  enableLoginDialog = false;
  enableJoinDialog = false;
  loginDialogOpen$: Observable<boolean>;
  cookiePolicy$: Observable<boolean>;
  mobileOrTablet$: Observable<boolean>;
  isLandingPage$: Observable<boolean>;
  welcomeBackMessageOpen$: Observable<boolean>;

  constructor(
    private navigationMenuService: NavigationMenuService,
    private languageService: LanguageService,
    private configService: ConfigService,
    private loginService: LoginService,
    private store$: Store<AppState>,
    private mediaQueryService: MediaQueryService,
    private gtmService: GtmService,
    private router: Router,
    private pageService: CmsPageService,
    private consentService: ConsentService,
    public joinService: JoinService
  ) {
    this.isVisible$ = this.navigationMenuService.isHeaderVisible$;
    this.isMenuOpen$ = this.navigationMenuService.isNavigationMenuOpen$;
    this.homeLink$ = this.languageService.lang.pipe(map((lang) => `/${lang}`));
    this.currentLanguage$ = this.languageService.langKey;
    this.countries$ = this.initListOfCountries();
    this.enableWelcomeBackMessage = this.configService.cfg.enableWelcomeBackMessage;
    this.enableLoginDialog = this.configService.cfg.enableLoginDialog;
    this.enableJoinDialog = this.configService.cfg.enableJoinDialog;
  }

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    this.oneworldLink$ = this.navigationMenuService.oneworldLink$;
    this.menuItems$ = this.navigationMenuService.menuItems$;
    this.expandedCategory$ = this.navigationMenuService.expandedNaviCategory;
    this.expandedOrActiveNaviCategory$ = this.navigationMenuService.expandedOrActiveNaviCategory$;
    this.activeNaviCategory$ = this.navigationMenuService.activeNaviCategory;
    const pagePath$ = this.router.events.pipe(
      filter<NavigationEnd>((event) => event instanceof NavigationEnd),
      map((navigation) => navigation.url),
      startWith(this.router.url),
      distinctUntilChanged()
    );

    this.pageHasDisruptions$ = pagePath$.pipe(
      switchMap((path) => this.pageService.getPageDataIfExists(path)),
      filter<CmsTemplate>(Boolean),
      map((template) => template.main),
      map((contents) =>
        contents.find(
          ({ viewTypeName }) => isPresent(viewTypeName) && viewTypeName.endsWith(CmsViewType.FRONT_PAGE_DISRUPTIONS)
        )
      ),
      map((disruptions) => disruptions?.items.length > 0),
      startWith(false)
    );

    this.cookiePolicy$ = this.consentService.cookieConsents$.pipe(
      map((cg) => cg?.consents?.every((c) => c.consentStatus !== ConsentStatus.WITHDRAWN)),
      startWith(false),
      finShare()
    );

    this.loginDialogOpen$ = this.store$.pipe(loginIsOpen());
    this.mobileOrTablet$ = combineLatest([
      this.mediaQueryService.isBreakpoint$('mobile'),
      this.mediaQueryService.isBreakpoint$('tablet'),
    ]).pipe(
      map(([mobile, tablet]) => mobile || tablet),
      distinctUntilChanged(),
      startWith(false)
    );

    this.isLandingPage$ = pagePath$.pipe(map((routeValue) => new RegExp('^/[^/]+$').test(routeValue)));

    this.welcomeBackMessageOpen$ = combineLatest([
      this.loginDialogOpen$,
      this.joinService.joinDialogOpen$,
      this.pageHasDisruptions$,
      this.cookiePolicy$,
      of(this.enableWelcomeBackMessage),
      this.loginStatus$,
      this.isLandingPage$,
    ]).pipe(
      map(
        ([
          loginDialogOpen,
          joinDialogOpen,
          pageHasDisruptions,
          cookiePolicy,
          enableWelcomeBackMessage,
          loginStatus,
          isLandingPage,
        ]) =>
          !loginDialogOpen &&
          !joinDialogOpen &&
          !pageHasDisruptions &&
          cookiePolicy &&
          enableWelcomeBackMessage &&
          loginStatus === LoginStatus.NOT_LOGGED_IN &&
          isLandingPage
      )
    );
  }

  openLoginWidget(event: MouseEvent): void {
    this.loginWidget.open();
    stopPropagation(event);
  }

  redirectToLogin(): void {
    this.loginService.redirectToLogin();
  }

  closeNaviMenu(): void {
    this.navigationMenuService.closeNaviMenu();
    this.isOpen = false;
  }

  setCategoryAsExpanded(event: Event, item: MenuItem): void {
    this.navigationMenuService.setCategoryAsExpanded(event, item);
  }

  trackItems(index: number): number {
    return index;
  }

  clickOutside(): void {
    this.isOpen = false;
    this.navigationMenuService.closeNaviMenu();
  }

  toggleNavigation(event: Event): void {
    stopPropagation(event);

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.navigationMenuService.setExpandedCategory(snapshot(this.expandedOrActiveNaviCategory$));
    } else {
      this.navigationMenuService.closeNaviMenu();
    }

    this.gtmService.trackElement(
      'desktop-main-navigation-hamburger-navi',
      GaContext.DESKTOP_MAIN_NAVIGATION,
      ElementTypes.ACCORDION,
      undefined,
      this.isOpen ? ElementActions.OPEN : ElementActions.CLOSE
    );
  }

  joinFinnairPlus(): void {
    if (this.enableJoinDialog) {
      this.loginWidget.close();
      this.joinService.joinDialogOpen$.next(true);
    } else {
      this.loginService.redirectToJoin();
    }
  }

  openLoginDialog(): void {
    this.loginWidget.close();
    this.joinService.joinDialogOpen$.next(false);
    this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
  }

  closeLoginDialog(): void {
    this.store$.dispatch(LoginActions.clearLoginPhase());
  }

  private initListOfCountries(): Observable<CountryState> {
    return this.languageService.countryCode.pipe(
      map(
        (countryCode) =>
          allowedConfs.find((country) => country.code === countryCode) || {
            languages: [],
            name: '',
            code: '',
          }
      )
    );
  }
}
